import {
    type ReactElement,
    type Ref,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    type ExpandedState,
    functionalUpdate,
    type OnChangeFn,
    type RowSelectionState,
    type SortingState,
} from "@tanstack/react-table";
import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import {
    ServiceTemplateSchedulesSortValue,
    type ServiceTemplateSchedulesView,
} from "@common/types/views/serviceTemplateSchedulesView";
import type { View } from "@common/types/views/views";
import {
    DataGrid,
    type DataGridHandle,
    type DataGridRow,
    type DataGridVirtualizationOptions,
    toViewSorting,
    toViewSortValue,
    useDataGrid,
    useSavedViewState,
} from "@design-system/DataGrid";
import { useDynamicTopOffset } from "@design-system/hooks/useDynamicTopOffset";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { styleWithVars } from "@lib/cssVars";
import { useScope } from "@state/scopeContext.lib";
import type { ServiceTemplateScheduleDisplayStatus } from "../serviceTemplateScheduleDisplayStatus";
import { ServiceTemplateScheduleOccurrencesDrawer } from "../ServiceTemplateScheduleOccurrencesDrawer";
import { ServiceTemplateSchedulesEmptyState } from "../ServiceTemplateSchedulesEmptyState";
import {
    serviceTemplateSchedulesViewConfig,
    type ServiceTemplateScheduleLeafRow,
    type ServiceTemplateScheduleRow,
} from "../serviceTemplateSchedulesViewConfig";
import { useServiceTemplateScheduleBulkActions } from "../useServiceTemplateScheduleBulkActions";
import { ScheduleListFooter } from "./ScheduleListFooter";
import {
    buildScheduleListColumns,
    getScheduleListSubRows,
} from "./scheduleListColumns";
import {
    type ScheduleListQuery,
    useScheduleListData,
} from "./useScheduleListData";
import { useScheduleRowActions } from "./useScheduleRowActions";
import styles from "./ServiceTemplateSchedulesTable.module.scss";

/** The server's own order, which clearing a sort returns to (PRO-10827). */
export const DEFAULT_SCHEDULE_SORT =
    ServiceTemplateSchedulesSortValue.NEXT_APPLICATION_DATE_ASC;

const SORT_COLUMNS = serviceTemplateSchedulesViewConfig.columns;

const getRowId = (row: ServiceTemplateScheduleRow) => row.id;

const toScheduleRows = (
    rows: readonly ServiceTemplateScheduleRow[],
): ServiceTemplateScheduleLeafRow[] =>
    rows.flatMap((row) => (row.rowType === "group" ? row.children : [row]));

/** Expanded ids of groups still listed; filtering keeps none of the rest. */
const toListedExpanded = ({
    expanded,
    rows,
}: {
    expanded: Record<string, boolean>;
    rows: readonly ServiceTemplateScheduleRow[];
}) => {
    const groupIds = new Set(
        rows.flatMap((row) => (row.rowType === "group" ? [row.id] : [])),
    );
    const listedIds = Object.keys(expanded).filter((id) => groupIds.has(id));
    return listedIds.length === Object.keys(expanded).length
        ? expanded
        : Object.fromEntries(listedIds.map((id) => [id, expanded[id]]));
};

export interface ServiceTemplateSchedulesTableProps {
    view: ServiceTemplateSchedulesView;
    /** The view column settings compare against; hidden columns keep its order. */
    defaultView: View;
    onViewChange: (view: ServiceTemplateSchedulesView) => void;
    /** Server-side filters; the list comes back sorted by `sort`. */
    query: Omit<ScheduleListQuery, "sort">;
    sort: ServiceTemplateSchedulesSortValue;
    onSortChange: (sort: ServiceTemplateSchedulesSortValue) => void;
    /** Display statuses to keep; empty keeps every schedule. */
    statusFilter: readonly ServiceTemplateScheduleDisplayStatus[];
    expanded: ExpandedState;
    onExpandedChange: (expanded: ExpandedState) => void;
    hasActiveFilters: boolean;
    onClearFilters: () => void;
    ref?: Ref<DataGridHandle>;
    virtualization?: DataGridVirtualizationOptions;
}

/**
 * The organization's schedules on DataGrid, sorted by the server, with
 * schedules sharing a group nested under it. Clicking or pressing Enter on a
 * schedule opens its occurrences; on a group it expands or collapses.
 */
export const ServiceTemplateSchedulesTable = ({
    view,
    defaultView,
    onViewChange,
    query,
    sort,
    onSortChange,
    statusFilter,
    expanded,
    onExpandedChange,
    hasActiveFilters,
    onClearFilters,
    ref,
    virtualization,
}: Readonly<ServiceTemplateSchedulesTableProps>): ReactElement => {
    const { organizationId } = useScope();
    const navigate = useStateAwareNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const { topOffset } = useDynamicTopOffset({ containerRef });
    const { rows, scheduleCount, schedulesById, lookups, status } =
        useScheduleListData({ query: { ...query, sort }, statusFilter });

    const [openScheduleId, setOpenScheduleId] = useState<string | null>(null);
    const { callbacks, isUpdatingStatus, confirmationModal } =
        useScheduleRowActions({ onOpenSchedule: setOpenScheduleId });
    const columns = useMemo(
        () =>
            buildScheduleListColumns({ lookups, callbacks, isUpdatingStatus }),
        [lookups, callbacks, isUpdatingStatus],
    );
    const savedViewState = useSavedViewState({
        view,
        onViewChange,
        columns,
        defaultView,
    });

    const sorting = useMemo(
        () => toViewSorting({ sort, columns: SORT_COLUMNS }),
        [sort],
    );
    const handleSortingChange: OnChangeFn<SortingState> = (updater) =>
        onSortChange(
            toViewSortValue({
                sorting: functionalUpdate(updater, sorting),
                columns: SORT_COLUMNS,
            }) ?? DEFAULT_SCHEDULE_SORT,
        );
    const handleExpandedChange: OnChangeFn<ExpandedState> = (updater) =>
        onExpandedChange(functionalUpdate(updater, expanded));

    // Ids of groups that filtering removed would keep Expand all reading
    // "Collapse all" over collapsed rows (PRO-10827).
    useEffect(() => {
        if (!rows || expanded === true) {
            return;
        }
        const listedExpanded = toListedExpanded({ expanded, rows });
        if (listedExpanded !== expanded) {
            onExpandedChange(listedExpanded);
        }
    }, [rows, expanded, onExpandedChange]);

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const selectedRows = useMemo(
        () => toScheduleRows(rows ?? []).filter((row) => rowSelection[row.id]),
        [rows, rowSelection],
    );
    const clearSelection = () => setRowSelection({});
    const bulkActions = useServiceTemplateScheduleBulkActions({
        organizationId,
        selectedRows,
        clearSelection,
    });

    const handleRowOpen = (row: DataGridRow<ServiceTemplateScheduleRow>) =>
        row.original.rowType === "schedule"
            ? setOpenScheduleId(row.original.id)
            : row.toggleExpanded();

    const table = useDataGrid({
        data: rows ?? undefined,
        columns,
        getRowId,
        status,
        noun: "schedules",
        expanding: {
            state: expanded,
            onChange: handleExpandedChange,
            getSubRows: getScheduleListSubRows,
            parentRows: "summary",
        },
        sorting: {
            mode: "server",
            state: sorting,
            onChange: handleSortingChange,
        },
        rowSelection: {
            state: rowSelection,
            onChange: setRowSelection,
            subRows: "cascade",
        },
        onRowOpen: handleRowOpen,
        virtualization,
        ...savedViewState,
    });
    const { activeRowKey } = table.atoms;
    useEffect(() => {
        activeRowKey.set(openScheduleId);
    }, [activeRowKey, openScheduleId]);
    const openSchedule =
        openScheduleId === null
            ? null
            : (schedulesById.get(openScheduleId) ?? null);

    const bulkDeleteCount = bulkActions.deleteEligibleCount;
    const closeBulkDelete = () => bulkActions.setShowDeleteConfirm(false);

    return (
        <div
            ref={containerRef}
            className={styles.root}
            style={styleWithVars({
                "--schedule-list-top-offset": `${topOffset}px`,
            })}
        >
            <DataGrid
                ref={ref}
                table={table}
                aria-label="Schedules"
                className={styles.table}
            >
                <DataGrid.Grid />
                <DataGrid.Empty>
                    <ServiceTemplateSchedulesEmptyState
                        hasActiveFilters={hasActiveFilters}
                        onClearFilters={onClearFilters}
                        onGoToTemplates={() => navigate("/templates/services")}
                    />
                </DataGrid.Empty>
                <DataGrid.Error />
                <div className={styles.footer}>
                    {scheduleCount > 0 && (
                        <ScheduleListFooter
                            scheduleCount={scheduleCount}
                            selectedCount={selectedRows.length}
                            onDeselect={clearSelection}
                            onOpenBulkActions={
                                bulkActions.handleOpenBulkActions
                            }
                            isBulkActionPending={
                                bulkActions.isBulkActionPending
                            }
                        />
                    )}
                </div>
            </DataGrid>

            <ServiceTemplateScheduleOccurrencesDrawer
                open={openScheduleId !== null}
                schedule={openSchedule}
                onClose={() => setOpenScheduleId(null)}
            />
            {confirmationModal}
            <ConfirmationModal
                show={bulkActions.showDeleteConfirm}
                title={`Delete ${pluralizeWithCount("schedule", bulkDeleteCount)}?`}
                question={`This will permanently remove the selected ${pluralize("schedule", bulkDeleteCount)} and their upcoming occurrences. This action cannot be undone.`}
                yes="Delete"
                yesBtnVariant="destructive"
                no="Cancel"
                onConfirm={bulkActions.handleBulkDeleteConfirm}
                onReject={closeBulkDelete}
                onHide={closeBulkDelete}
            />
        </div>
    );
};
